import React, { useState, useEffect } from 'react';
import { MenuItem, Select, FormControl, InputLabel, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getAllAssignments, deleteAssignment } from '../../../services/apiservice';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

const NoData = () => <span style={{ color: '#ccc' }}>No data</span>;

function AssignmentList() {
  const theme = useTheme();
  const { data: fetchedAssignments, loading } = useApiData(() => getAllAssignments().then((res) => res.data), []);
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState({});
  const [statusFilter, setStatusFilter] = useState('All');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    if (fetchedAssignments) {
      setAssignments(fetchedAssignments);
    }
  }, [fetchedAssignments]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleDelete = async (assignment) => {
    const assignmentId = assignment.AssignmentID;
    try {
      setLoadingAssignments(prevState => ({ ...prevState, [assignmentId]: true }));
      const response = await deleteAssignment(assignmentId);
      if (response.success) {
        setAssignments(assignments.filter(a => a.AssignmentID !== assignmentId));
        setSnackbarMessage('Assignment Deleted successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.message || 'Failed to delete Assignment.');
        setSnackbarSeverity('error');
      }
    }
    catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setLoadingAssignments(prevState => ({ ...prevState, [assignmentId]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Assigned':
        return { backgroundColor: theme.palette.warning.main, color: theme.palette.warning.contrastText };
      case 'Active':
        return { backgroundColor: theme.palette.success.main, color: theme.palette.success.contrastText };
      default:
        return { backgroundColor: theme.palette.neutral.main, color: theme.palette.neutral.contrastText };
    }
  };

  const columns = [
    { key: 'AssignmentUserID', label: 'User ID', width: 100 },
    { key: 'AssignmentCredentialID', label: 'Credential ID', width: 100 },
    { key: 'AssignmentDate', label: 'Date', width: 140 },
    {
      key: 'AssignmentStatus',
      label: 'Status',
      width: 100,
      render: (row) => (
        <Chip
          label={row.AssignmentStatus}
          size="small"
          sx={{ backgroundColor: getStatusColor(row.AssignmentStatus).backgroundColor, color: theme.palette.common.white }}
        />
      ),
    },
    {
      key: 'AssignmentIsPublishedOnLinkedIn',
      label: 'Published on LinkedIn',
      width: 150,
      render: (row) => (row.AssignmentIsPublishedOnLinkedIn ? 'Yes' : 'No'),
    },
  ];

  const statusToolbar = (
    <FormControl variant="outlined" size="small" sx={{ minWidth: 120, marginLeft: 2 }}>
      <InputLabel>Status</InputLabel>
      <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
        <MenuItem value="All">All</MenuItem>
        <MenuItem value="Assigned">Assigned</MenuItem>
        <MenuItem value="Active">Active</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Assignments List"
        columns={columns}
        rows={assignments}
        loading={loading}
        searchKeys={['AssignmentUserID', 'AssignmentCredentialID', 'AssignmentStatus']}
        rowKey="AssignmentID"
        onDelete={handleDelete}
        deleting={loadingAssignments}
        toolbar={statusToolbar}
        filterFn={(a) => statusFilter === 'All' || statusFilter.toLowerCase() === String(a.AssignmentStatus ?? '').toLowerCase()}
      />
    </>
  );
}

export default AssignmentList;
